import { motion } from 'motion/react';
import { ReactNode } from 'react';
import { IconType } from 'react-icons';
import { AiOutlineInstagram } from 'react-icons/ai';
import { FaFacebookF, FaTwitter } from 'react-icons/fa';
import { MdAlternateEmail, MdLocationOn, MdPhone } from 'react-icons/md';
import { TbWorld } from 'react-icons/tb';

import { Link } from '@/components/Link';

interface ContactItem {
  key: string;
  icon: IconType;
  element: ReactNode;
  shouldHighlight: boolean;
  shouldRender: boolean;
}

export type ContactProps = {
  address: string;
  storeProfile: AdaptedStore<true>['profile'];
  content: StoreProfilePageContent;
};

export function Contact({ address, storeProfile, content }: ContactProps) {
  const contactItems: ContactItem[] = [
    {
      key: 'address',
      icon: MdLocationOn,
      element: address,
      shouldHighlight: false,
      shouldRender: Boolean(address),
    },
    {
      key: 'phone',
      icon: MdPhone,
      element: (
        <Link href={`tel:${storeProfile.phone}`} isExternal>
          {storeProfile.phone}
        </Link>
      ),
      shouldHighlight: true,
      shouldRender: Boolean(storeProfile.phone),
    },
    {
      key: 'email',
      icon: MdAlternateEmail,
      element: (
        <Link href={`mailto:${storeProfile.email}`} isExternal>
          {storeProfile.email}
        </Link>
      ),
      shouldHighlight: true,
      shouldRender: Boolean(storeProfile.email),
    },
    {
      key: 'www',
      icon: TbWorld,
      element: (
        <Link href={storeProfile.www} target="_blank" isExternal>
          {storeProfile.www}
        </Link>
      ),
      shouldHighlight: true,
      shouldRender: Boolean(storeProfile.www),
    },
    {
      key: 'instagram',
      icon: AiOutlineInstagram,
      element: (
        <Link
          href={storeProfile.socialMedia.instagram}
          target="_blank"
          isExternal
        >
          {content.profile.instagram}
        </Link>
      ),
      shouldHighlight: true,
      shouldRender: Boolean(storeProfile.socialMedia.instagram),
    },
    {
      key: 'facebook',
      icon: FaFacebookF,
      element: (
        <Link
          href={storeProfile.socialMedia.facebook}
          target="_blank"
          isExternal
        >
          {content.profile.facebook}
        </Link>
      ),
      shouldHighlight: true,
      shouldRender: Boolean(storeProfile.socialMedia.facebook),
    },
    {
      key: 'twitter',
      icon: FaTwitter,
      element: (
        <Link
          href={storeProfile.socialMedia.twitter}
          target="_blank"
          isExternal
        >
          {content.profile.twitter}
        </Link>
      ),
      shouldHighlight: true,
      shouldRender: Boolean(storeProfile.socialMedia.twitter),
    },
  ];

  const validContactItems = contactItems.filter(item => item.shouldRender);

  return (
    <div className="flex flex-col items-start space-y-2.5">
      <h2 className="text-xl">{content.profile.contact}</h2>

      <div className="flex w-full flex-row flex-wrap">
        {validContactItems.map(
          ({ element, icon: Icon, key, shouldHighlight }) => {
            return (
              <motion.div
                key={key}
                className="mt-4 mr-4 flex items-center space-x-2"
                whileHover={shouldHighlight ? { scale: 1.05 } : {}}
                whileTap={shouldHighlight ? { scale: 0.95 } : {}}
              >
                <div className="bg-store-primary flex items-center justify-center rounded-sm p-0.5 shadow-lg">
                  <Icon className="text-xl text-white" />
                </div>

                <span className="text-sm whitespace-nowrap">{element}</span>
              </motion.div>
            );
          },
        )}
      </div>
    </div>
  );
}
